import { useState, type FormEvent } from "react";
import { siteContact } from "@/mocks/gossip";
import SectionHeading from "./SectionHeading";

const FORM_URL = "https://readdy.ai/api/form/daglon3ep053ijub2d2g";

const statusMeta = {
  idle: null,
  success: "收到啦！感谢你提供线索，我们会尽快核对并更新对应时间线。",
} as const;

export default function Contact() {
  const [status, setStatus] = useState<{ code: "success" | "error"; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    setBusy(true);
    setStatus(null);

    const honey = String(fd.get("contact_alt") || "").trim();
    if (honey) {
      form.reset();
      setStatus({ code: "success", msg: statusMeta.success });
      setBusy(false);
      return;
    }

    const params = new URLSearchParams();
    for (const [key, value] of fd.entries()) {
      if (key === "contact_alt") continue;
      const v = String(value).trim();
      if (v) params.append(key, v);
    }

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (res.ok ? "" : "提交失败，请稍后重试。");
      const spamHit =
        !res.ok ||
        !parsed?.code ||
        serverMsg.toLowerCase().includes("spam") ||
        responseText.toLowerCase().includes("spam");

      if (spamHit) {
        setStatus({
          code: "error",
          msg: serverMsg || "内容未通过校验，请检查后重新提交。",
        });
        return;
      }
      form.reset();
      setStatus({ code: "success", msg: statusMeta.success });
    } catch {
      setStatus({ code: "error", msg: "网络异常，暂时无法提交，请稍后再试。" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section id="contact" className="relative overflow-hidden bg-background-100 py-14 md:py-20">
      <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary-100/60 blur-2xl"></div>
      <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-accent-100/60 blur-2xl"></div>

      <div className="container-x relative grid gap-10 lg:grid-cols-2 lg:items-center">
        <div>
          <SectionHeading
            eyebrow="Contact"
            title="补充线索 / 联系我们"
            desc="发现时间线有遗漏、顺序有出入，或手上有值得跟进的新线索？欢迎告诉我们。编辑部会根据公开信息核实后更新，让脉络更完整。"
          />
          <ul className="mt-8 space-y-4">
            <li className="flex items-center gap-3 text-sm text-foreground-700">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-foreground-50">
                <i className="ri-mail-send-line text-lg"></i>
              </span>
              联系邮箱：<a href={`mailto:${siteContact.email}`} className="font-medium text-primary-700 underline-offset-4 hover:underline">{siteContact.email}</a>
            </li>
            <li className="flex items-center gap-3 text-sm text-foreground-700">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
                <i className="ri-chat-smile-2-line text-lg"></i>
              </span>
              官方账号：<span className="font-medium text-foreground-900">{siteContact.weibo}</span>
            </li>
            <li className="flex items-center gap-3 text-sm text-foreground-700">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-foreground-50">
                <i className="ri-time-line text-lg"></i>
              </span>
              回复时效：<span className="font-medium text-foreground-900">通常 1–2 个工作日内</span>
            </li>
          </ul>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form
          className="rounded-2xl bg-background-50 p-6 ring-1 ring-background-200 md:p-8"
        >
          <h3 className="font-heading text-xl font-bold text-foreground-950">提交你的反馈</h3>
          <p className="mt-2 text-sm text-foreground-500">带 * 为必填项，请放心填写，信息仅用于核对与回复。</p>

          {/* anti-bot honeypot */}
          <input type="text" name="contact_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                你的称呼 <span className="text-primary-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                maxLength={30}
                placeholder="怎么称呼你"
                className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                联系邮箱 <span className="text-primary-500">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="用于回复处理结果"
                className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
              反馈类型
            </label>
            <select
              id="topic"
              name="topic"
              defaultValue="补充时间线索"
              className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            >
              <option value="补充时间线索">补充时间线索</option>
              <option value="指出信息有误">指出信息有误</option>
              <option value="提供新线索">提供新线索</option>
              <option value="其他咨询">其他咨询</option>
            </select>
          </div>

          <div className="mt-4">
            <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
              补充说明 <span className="text-primary-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              maxLength={500}
              rows={5}
              placeholder="请尽量写清事件发生的大致时间、来源与关键节点……"
              className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            ></textarea>
            <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-foreground-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i> 提交中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-line"></i> 提交反馈
              </>
            )}
          </button>

          {status && (
            <div
              role="alert"
              className={`mt-4 flex items-start gap-2 rounded-lg px-4 py-3 text-sm leading-6 ${
                status.code === "success"
                  ? "bg-accent-100 text-accent-900"
                  : "bg-primary-100 text-primary-900"
              }`}
            >
              <i className={`mt-0.5 ${status.code === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}`}></i>
              <span>{status.msg}</span>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}